import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Row,
  Table,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { get, omitBy } from 'lodash';
import queryString from 'query-string';

import PdButton from 'components/pd-button';
import PdCsvDownloader from 'components/pd-csv-downloader';
import { PdCsvUploader } from 'components/pd-csv-uploader';
import PdUploader from 'components/pd-uploader';

import BrandService from 'services/brand';
import CategoryService from 'services/category';
import ProductService from 'services/product';
import RentService from 'services/rent';
import WarehouseService from 'services/warehouse';
import { errHandling, priceFormat, isNil } from 'utils';

import {
  LIST_CATEGORIES_COLUMNS,
  LIST_BRANDS_COLUMNS,
  LIST_PRODUCTS_COLUMNS,
  TEMPLATES_PRODUCT,
} from './constant';

import './style.scss';
import useClickOutside from 'hooks/useClickOutside';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Content } = Layout;
const { Search } = Input;
const { confirm } = Modal;

const FIELDS = {
  DESKTOP_MEDIA_URL: 'banner_image',
  MOBILE_MEDIA_URL: 'mobile_image',
};

export function Product() {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadStatus, setLoadStatus] = useState(true);
  const [maxPrice, setMaxPrice] = useState(0);
  const [minPrice, setMinPrice] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [productList, setProductList] = useState([]);
  const [rentProp, setRentProp] = useState({});
  const [rentPropModal, setRentPropModal] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [showModalBanner, setShowModalBanner] = useState(false);

  const [totalData, setTotalData] = useState(0);
  const [warehouseList, setWarehouseList] = useState([]);
  const [open, setOpen] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(false);

  const bulkRef = useRef();
  const downloadRef = useRef();
  const templateRef = useRef();

  const parsed = queryString.parse(get(location, 'search', ''));
  const currentLocation = get(location, 'pathname', '/products');
  const isRentPage = currentLocation === '/rent_products';

  const handleInputNumberParser = (value) => {
    return value.replace(/[^0-9]\s?|(\.*)/g, '').replace(/^0+/, '');
  };

  const handleInputNumberFormatter = (value) => {
    return `IDR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  async function hideProduct(id) {
    try {
      setLoadStatus(true);
      if (id) {
        await ProductService.hideProduct(id);
        message.success('successfully hide product');
      }
    } catch (err) {
      errHandling(err);
    } finally {
      fetchProductList(
        parsed['page'],
        parsed['title'],
        parsed['sortBy'],
        pageSize,
        parsed['status'],
        parsed['minPrice'],
        parsed['maxPrice']
      );
      setLoadStatus(false);
    }
  }

  async function deleteProduct(id) {
    try {
      setLoadStatus(true);
      if (id) {
        await ProductService.deleteProduct(id);
        message.success('Successfully removed product');
      }
    } catch (err) {
      errHandling(err);
    } finally {
      fetchProductList(
        parsed['page'],
        parsed['title'],
        parsed['sortBy'],
        pageSize,
        parsed['status'],
        parsed['minPrice'],
        parsed['maxPrice']
      );
      setLoadStatus(false);
    }
  }

  async function handleDeactivate(id) {
    confirm({
      title: 'Are you sure you want to deactivate this product?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hideProduct(id);
      },
      onCancel() {},
    });
  }

  async function handleDelete(id) {
    confirm({
      title: 'Are you sure you want to delete?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteProduct(id);
      },
      onCancel() {},
    });
  }

  async function handleRestore(id) {
    try {
      setLoadStatus(true);
      if (id) {
        await ProductService.restoreProduct(id);
        message.success('Successfully restored product');
      }
    } catch (err) {
      errHandling(err);
    } finally {
      fetchProductList(
        parsed['page'],
        parsed['title'],
        parsed['sortBy'],
        pageSize,
        parsed['status'],
        parsed['minPrice'],
        parsed['maxPrice']
      );
      setLoadStatus(false);
    }
  }

  async function fetchProductList(
    page = 1,
    q = '',
    sort_by,
    limit = pageSize,
    status,
    minPrice,
    maxPrice
  ) {
    setLoadStatus(true);
    try {
      const { data } = await ProductService.getProductList({
        maxPrice,
        minPrice,
        page,
        q,
        sort_by,
        status,
        limit,
      });
      const productData = get(data, 'data', []);
      const rentData = get(data, 'data', []).filter(
        (product) => product.rented
      );

      setLoadStatus(false);
      setTotalData(data.metadata.total_items);
      currentLocation === '/rent_products'
        ? setProductList(rentData)
        : setProductList(productData);
    } finally {
      setLoadStatus(false);
      setOpenBulk(false);
      setOpen(false);
    }
  }

  async function getWarehouseList() {
    try {
      const {
        data: { data = [] },
      } = await WarehouseService.getWarehouseList({ status: 'active' });
      const newData = data.map((d) => ({
        id: d.title.toLowerCase().replace(/ /g, '_'),
        displayName: d.title.toLowerCase(),
      }));
      setWarehouseList(newData);
    } catch (error) {
      errHandling(error);
    }
  }

  async function getRentBannerProp() {
    try {
      const { data } = await RentService.getRentPageBanner();
      const rentBannerData = get(data, 'data', {});
      setRentProp(rentBannerData);
      setRentPropModal(rentBannerData);
    } catch (error) {
      errHandling(error);
    }
  }

  function filterProduct(param, val) {
    if (!val) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: currentLocation,
      search: stringified,
    });
  }

  function closeModal() {
    setRentPropModal(rentProp);
    setShowModalBanner(false);
  }

  async function handleSubmit(values) {
    try {
      setLoadingButton(true);
      await RentService.updateRentPageBanner({
        ...rentPropModal,
        ...values,
      });
      message.success('Successfully update banner');
      getRentBannerProp();
      setShowModalBanner(false);
    } catch (err) {
      message.error(
        get(
          err,
          'response.data.message',
          'Cannot connect to server, please check connection'
        )
      );
    } finally {
      setLoadingButton(false);
    }
  }

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function changeSearchValue(e) {
    setSearchValue(e.target.value);
  }

  function changeMinPrice(e) {
    setMinPrice(e === null ? 0 : e);
  }

  function changeMaxPrice(e) {
    setMaxPrice(e === null ? 0 : e);
  }

  function handleChangeRentPropModal(url, key) {
    setRentPropModal((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  async function handleDownloadCsv() {
    try {
      const {
        data: { data = [] },
      } = await ProductService.getProductReport();

      const expected = data.map((product) => {
        const expectedProduct = {
          ...product,

          rent_pricing:
            product.rent_pricing.length > 0
              ? product.rent_pricing
                  .map(
                    (rent) => `${rent.price} (${rent.rent_duration_id} Hari)`
                  )
                  .join(' , ')
              : null,
        };

        warehouseList.forEach((warehouse) => {
          const allDiscount = product.discount_prices.find(
            (discount) => discount.warehouse === null
          );

          if (allDiscount)
            return (expectedProduct[warehouse.id] = Math.floor(
              allDiscount.discount_price
            ));

          const discount = product.discount_prices.find(
            (discount) =>
              discount.warehouse.toLowerCase().replace(/ /g, '_') ===
              warehouse.id
          );
          if (discount) {
            expectedProduct[warehouse.id] = Math.floor(discount.discount_price);
          } else {
            expectedProduct[warehouse.id] = '';
          }
        });

        return expectedProduct;
      });

      setOpen(false);
      return expected
    } catch (error) {
      errHandling(error);
    }
  }

  /* eslint-disable */
  useEffect(() => {
    if (parsed['title']) {
      setSearchValue(parsed['title']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: currentLocation,
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    if (parsed['minPrice']) {
      if (parsed['minPrice'] < 1) {
        delete parsed['minPrice'];
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: currentLocation,
          search: stringified,
        });
      } else {
        setMinPrice(parsed['minPrice']);
      }
    } else {
      setMinPrice(0);
    }

    if (parsed['maxPrice']) {
      if (parsed['maxPrice'] < 1) {
        delete parsed['maxPrice'];
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: currentLocation,
          search: stringified,
        });
      } else {
        setMaxPrice(parsed['maxPrice']);
      }
    } else {
      setMaxPrice(0);
    }

    fetchProductList(
      parsed['page'],
      parsed['title'],
      parsed['sortBy'],
      pageSize,
      parsed['status'],
      parsed['minPrice'],
      parsed['maxPrice']
    );
  }, [
    parsed['title'],
    parsed['page'],
    parsed['sortBy'],
    parsed['status'],
    parsed['minPrice'],
    parsed['maxPrice'],
    currentLocation,
    pageSize
  ]);
  /* eslint-enable */

  useEffect(() => {
    getRentBannerProp();
    getWarehouseList();
  }, []);

  async function handleAfterUpload(arrayOfData) {
    try {
      const payload = arrayOfData
        .map(({ SKU: sku = '', ...rest } = {}) => ({
          sku,
          keyword: Object.values(rest).toString(),
        }))
        .filter(
          ({ sku = '', keyword = '' } = {}) => sku.trim() && keyword.trim()
        )
        .map(({ sku, keyword: _keyword = '' } = {}) => {
          const keyword = _keyword
            .split(',')
            .filter((str) => str !== '')
            .map((str = '') => str.trim())
            .join(', ');
          return { sku, keyword };
        });

      await ProductService.bulkUpdateProduct(payload);
      message.success('successfully updated keywords');
    } catch (error) {
      errHandling(error);
    }
  }

  function transformHeaderBulkCreate(header, index) {
    const BULK_CREATE_PRODUCT_FIELDS = [
      'sku',
      'title',
      'description',
      'category_id',
      'child_category_id',
      'sub_category_id',
      'brand_id',
      'weight',
      'limit_per_order',
      'image1url',
      'image2url',
      'image3url',
      'image4url',
      'image5url',
      'video_url',
      'status',
      'price',
      'keyword',
      'pos_only',
      'width',
      'height',
      'length',
      'diameter',
    ];
    header = BULK_CREATE_PRODUCT_FIELDS[index];
    return header;
  }

  async function handleAfterUploadBulkCreate(arrayOfData = []) {
    try {
      const payload = [];
      for (let i = 0; i < arrayOfData.length; i++) {
        const {
          brand_id,
          category_id,
          child_category_id,
          image1url,
          image2url,
          image3url,
          image4url,
          image5url,
          limit_per_order,
          price,
          status,
          sub_category_id,
          video_url,
          weight,
          pos_only,
          width,
          height,
          length,
          diameter,
          ...rest
        } = arrayOfData[i] || {};

        const product_galleries_attributes = [];
        const product_categories_attributes = [
          { category_id },
          { category_id: child_category_id },
          { category_id: sub_category_id },
        ].filter(({ category_id }) => Boolean(category_id));

        [image1url, image2url, image3url, image4url, image5url].forEach(
          (url, index) => {
            if (url)
              product_galleries_attributes.push({
                secure_url: url,
                thumbnail_image: url,
                title: `Image ${index + 1}`,
                url_type: 'image',
                order_level: 10 + index,
              });
          }
        );

        if (video_url) {
          product_galleries_attributes.push({
            secure_url: video_url,
            thumbnail_image: video_url,
            title: 'Video 1',
            url_type: 'video',
            order_level: 15,
          });
        }

        const lowerStatus = status.trim().toLowerCase();

        const dimensions = {
          width: +width || 0,
          height: +height || 0,
          length: +length || 0,
          diameter: +diameter || 0,
        };

        const cleanPrice = (price || '').replace(/\D/g, '');

        payload.push({
          brand_id: +brand_id,
          limit_per_order: +limit_per_order || 1,
          price: cleanPrice,
          product_categories_attributes,
          product_galleries_attributes,
          status: ['active', 'inactive'].includes(lowerStatus)
            ? lowerStatus
            : 'active',
          weight: +weight || 1000,
          pos_only: pos_only === 'iya',
          dimensions,
          ...rest,
        });
      }
      const { data: { warning = [] } = {} } =
        await ProductService.bulkCreateProduct(payload);
      if (warning.length) {
        return warning.forEach((err) => message.error(err));
      }
      message.success('Successfully added');
      fetchProductList(
        parsed['page'],
        parsed['title'],
        parsed['sortBy'],
        pageSize,
        parsed['status'],
        parsed['minPrice'],
        parsed['maxPrice']
      );
    } catch (error) {
      errHandling(error);
    }
  }

  const transformHeaderBulkDiscount = (header, index) => {
    const BULK_DISCOUNT_PRODUCT_FIELDS = [
      'sku',
      'title',
      'warehouse_id',
      'deduction_percentage',
      'lower_price',
      'quantity_limit',
      'start_date',
      'end_date',
      'whole_year',
    ];
    header = BULK_DISCOUNT_PRODUCT_FIELDS[index];
    return header;
  };

  const handleAfterUploadBulkDiscount = async (arrayOfData = []) => {
    try {
      const payload = [];
      for (let i = 0; i < arrayOfData.length; i++) {
        const {
          sku,
          title,
          warehouse_id,
          deduction_percentage,
          lower_price,
          quantity_limit,
          start_date,
          end_date,
          whole_year,
        } = arrayOfData[i] || {};

        const startDate = new Date(start_date);
        const endDate = new Date(end_date);

        // Memeriksa apakah tanggal formatnya valid
        if (isNaN(startDate) || isNaN(endDate)) {
          message.error('Invalid date format');
        }

        payload.push({
          sku,
          title,
          warehouse_id:
            warehouse_id.toLowerCase() === 'all' ? null : warehouse_id,
          deduction_percentage:
            Number(deduction_percentage) > 0
              ? Number(deduction_percentage)
              : null,
          lower_price:
            Number(lower_price) > 0
              ? Number((lower_price || '').replace(/\D/g, ''))
              : null,
          quantity_limit,
          start_date:
            whole_year === 'active'
              ? dayjs(`${dayjs().year()}-01-01`).format('YYYY-MM-DD HH:mm')
              : dayjs(startDate).format('YYYY-MM-DD HH:mm'),
          end_date:
            whole_year === 'active'
              ? dayjs(`${dayjs().year()}-12-31`).format('YYYY-MM-DD HH:mm')
              : dayjs(endDate).format('YYYY-MM-DD HH:mm'),
        });
      }

      const { data: { warning = [] } = {} } =
        await ProductService.bulkDiscountProduct(payload);
      if (warning.length) {
        return warning.forEach((err) => message.error(err));
      }
      message.success('Successfully added');
      fetchProductList(
        parsed['page'],
        parsed['title'],
        parsed['sortBy'],
        pageSize,
        parsed['status'],
        parsed['minPrice'],
        parsed['maxPrice']
      );
    } catch (error) {
      if (error.message !== 'Invalid time value') {
        errHandling(error);
      }
    }
  };

  async function downloadListCategory() {
    try {
      const { data: { data = [] } = {} } =
        await CategoryService.getCategoriesRelations();

      const parsedData = [];
      data.forEach(({ id, title, subcategories = [] }) => {
        const parent = { id, title };
        if (!subcategories.length) {
          parsedData.push(parent);
          return;
        }

        subcategories.forEach(
          (
            { id: subCategoryId, title: subCategoryTitle, subcategories = [] },
            index
          ) => {
            if (!index && !subcategories.length) {
              parsedData.push({ ...parent, subCategoryId, subCategoryTitle });
            } else if (!subcategories.length) {
              parsedData.push({ subCategoryId, subCategoryTitle });
            }
            subcategories.forEach(
              (
                { id: childCategoryId, title: childCategoryTitle },
                indexChild
              ) => {
                if (!indexChild && !index) {
                  parsedData.push({
                    ...parent,
                    childCategoryId,
                    childCategoryTitle,
                    subCategoryId,
                    subCategoryTitle,
                  });
                } else if (!indexChild) {
                  parsedData.push({
                    childCategoryId,
                    childCategoryTitle,
                    subCategoryId,
                    subCategoryTitle,
                  });
                } else {
                  parsedData.push({ childCategoryId, childCategoryTitle });
                }
              }
            );
          }
        );
      });
 setOpen(false);
      return parsedData
    } catch (error) {
      errHandling(error);
    }
  }

  async function downloadListBrand() {
    try {
      const { data: { data = [] } = {} } = await BrandService.getBrandList();
       setOpen(false);
      return data
    } catch (error) {
      errHandling(error);
    }
  }

  function transformHeaderBulkUpdate(header, index) {
    const BULK_CREATE_PRODUCT_FIELDS = [
      'sku',
      'title',
      'description',
      'category_id',
      'child_category_id',
      'sub_category_id',
      'brand_id',
      'weight',
      'limit_per_order',
      'image1url',
      'image2url',
      'image3url',
      'image4url',
      'image5url',
      'video_url',
      'status',
      'price',
      'keyword',
      'quantity',
      'warehouse_id',
      'pos_only',
      'width',
      'height',
      'length',
      'diameter',
    ];
    header = BULK_CREATE_PRODUCT_FIELDS[index];
    return header;
  }

  async function handleAfterUploadBulkUpdate(data = []) {
    try {
      const payload = [];
      for (let i = 0; i < data.length; i++) {
        const {
          brand_id,
          category_id,
          child_category_id,
          image1url,
          image2url,
          image3url,
          image4url,
          image5url,
          limit_per_order,
          price,
          status,
          sub_category_id,
          video_url,
          weight,
          pos_only,
          width,
          height,
          length,
          diameter,
          ...rest
        } = data[i] || {};

        const product_galleries_attributes = [];
        const product_categories_attributes = [
          { category_id },
          { category_id: child_category_id },
          { category_id: sub_category_id },
        ].filter(({ category_id }) => Boolean(category_id));

        [image1url, image2url, image3url, image4url, image5url].forEach(
          (url, index) => {
            if (url)
              product_galleries_attributes.push({
                secure_url: url,
                thumbnail_image: url,
                title: `Image ${index + 1}`,
                url_type: 'image',
                order_level: index + 1,
              });
          }
        );

        if (video_url) {
          product_galleries_attributes.push({
            secure_url: video_url,
            thumbnail_image: video_url,
            title: 'Video 1',
            url_type: 'video',
            order_level: 15,
          });
        }

        const lowerStatus = status.trim().toLowerCase();
        const dimensions = {
          width: +width || 0,
          height: +height || 0,
          length: +length || 0,
          diameter: +diameter || 0,
        };

        const cleanPrice = (price || '').replace(/\D/g, '');

        payload.push({
          brand_id,
          limit_per_order,
          price: cleanPrice,
          product_categories_attributes,
          product_galleries_attributes,
          status: ['active', 'inactive'].includes(lowerStatus)
            ? lowerStatus
            : '',
          weight,
          pos_only: pos_only === 'iya',
          dimensions,
          ...rest,
        });
      }
      const omittedPayload = payload.map((product) => omitBy(product, isNil));
      const { data: { warning = [] } = {} } =
        await ProductService.bulkUpdateProduct(omittedPayload);
      if (warning.length) {
        return warning.forEach((err) => message.error(err));
      }
      message.success('Successfully added');
      fetchProductList(
        parsed['page'],
        parsed['title'],
        parsed['sortBy'],
        pageSize,
        parsed['status'],
        parsed['minPrice'],
        parsed['maxPrice']
      );
    } catch (error) {
      errHandling(error);
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '45%',
      render: function Title(_text, row) {
        return (
          <div
            className={
              row.discounted_price !== '0'
                ? 'product-container--expanded'
                : 'product-container--default'
            }>
            <div className="table-title--data fs-14">{row.title}</div>
            {row.sku !== null && <div className="fs-12">SKU: {row.sku}</div>}
          </div>
        );
      },
    },
    {
      title: 'Base Price',
      dataIndex: 'price',
      key: 'price',
      render: function BasePrice(_text, row) {
        return (
          <div>
            {row.discounted_price !== row.price &&
              row.discounted_price !== '0' && (
                <div className="fs-14 spacing">
                  IDR {priceFormat(row.discounted_price)}
                </div>
              )}
            {row.discounted_price !== row.price &&
            row.discounted_price !== '0' ? (
              <strike className="fs-12 spacing">
                IDR {priceFormat(row.price)}
              </strike>
            ) : (
              <div className="fs-14 defaultPrice">
                IDR {priceFormat(row.price)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      ...(parsed['status'] && {
        defaultFilteredValue: [parsed['status']],
      }),
      render: function Status(_text, row) {
        return (
          <div className="status-indicator">
            <span className={row.status !== 'inactive' ? 'active' : 'hidden'}>
              {displayStatus(row.status)}
            </span>
          </div>
        );
      },
      filters: [
        { text: 'Active', value: 'active' },
        {
          text: 'Inactive',
          value: 'inactive',
        },
      ],
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'slug',
      render: function Slug(_text, row) {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
            {row.status === 'active' ? (
              <PdButton
                className="custom-button-table w-90"
                onClick={handleDeactivate.bind(this, row.id)}
                style={{
                  width: 'fit-content',
                }}
                outline
                type="danger">
                Deactivate
              </PdButton>
            ) : (
              <PdButton
                className="custom-button-table w-90"
                onClick={handleRestore.bind(this, row.id)}
                outline
                type="primary">
                Reactivate
              </PdButton>
            )}

            {row.status === 'active' ? (
              <Link
                to={{
                  pathname: '/products/edit',
                  search: `id=${row.id}`,
                  state: { prevPath: '/products', parsed },
                }}>
                <PdButton className="custom-button-table w-90" type="primary">
                  Edit
                </PdButton>
              </Link>
            ) : (
              <PdButton
                onClick={handleDelete.bind(this, row.id)}
                className="custom-button-table w-90"
                type="danger">
                Delete
              </PdButton>
            )}
          </div>
        );
      },
    },
  ];

  const handleTableChange = (pagination, filters) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize)
    parsed['page'] = pagination.current;
    parsed['pageSize'] = pagination.pageSize;
    if (filters.status && filters.status.length < 2) {
      parsed['status'] = filters.status;
    } else {
      delete parsed['status'];
    }

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: currentLocation,
      search: stringified,
    });
  };

  useClickOutside(bulkRef, () => setOpenBulk(false));
  useClickOutside(downloadRef, () => setOpen(false));
  useClickOutside(templateRef, () => setOpenTemplate(false));

  return (
    <Layout className="pd-cms-products main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Products</div>
          <Content className="pageContent">
            {isRentPage && (
              <Row className="banner-container" gutter={[60, 10]}>
                <Col xs={24}>
                  <span className="title-banner">Banner Image</span>
                </Col>
                <Col xs={14}>
                  <span className="desc-banner">Desktop (1200x200 px)</span>
                  <div
                    className="preview-banner"
                    style={
                      rentProp.banner_image && {
                        backgroundColor: '#fff',
                        justifyContent: 'flex-start',
                      }
                    }>
                    {rentProp.banner_image ? (
                      <img
                        alt="img"
                        className="preview-img"
                        src={rentProp.banner_image}
                      />
                    ) : (
                      <span className="img-alt">No image available</span>
                    )}
                  </div>
                </Col>
                <Col xs={10}>
                  <span className="desc-banner">Mobile (640x320 px)</span>
                  <div
                    className="preview-banner mobile"
                    style={
                      rentProp.banner_image && {
                        backgroundColor: '#fff',
                        justifyContent: 'flex-start',
                      }
                    }>
                    {rentProp.mobile_image ? (
                      <img
                        alt="img"
                        className="preview-img"
                        src={rentProp.mobile_image}
                      />
                    ) : (
                      <span className="img-alt">No image available</span>
                    )}
                  </div>
                </Col>
                <Col xs={24}>
                  <PdButton
                    className="custom-button w-10"
                    onClick={setShowModalBanner.bind(this, true)}
                    type="primary">
                    Edit
                  </PdButton>
                </Col>
              </Row>
            )}

            <Row gutter={[8, 0]}>
              <Col xs={18}>
                <Search
                  className="search-product"
                  enterButton
                  onChange={changeSearchValue}
                  onSearch={filterProduct.bind(this, 'title')}
                  placeholder="Search Product List"
                  value={searchValue}
                />
              </Col>
              <Col xs={{ offset: 1, span: 3 }}>
                <Link
                  to={{
                    pathname: `/products/create`,
                    state: { prevPath: '/products', parsed },
                  }}>
                  <PdButton
                    className="custom-button-add-new-product"
                    type="primary"
                    style={{
                      height: '35px',
                    }}
                    full>
                    Add New Product
                  </PdButton>
                </Link>
              </Col>
            </Row>
            <Divider
              style={{
                margin: '16px 0px 12px',
              }}
            />

            <Row gutter={[16, 8]}>
              <Col xs={9} className="pd-margin-top-sm">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                  }}>
                  <InputNumber
                    className="min-max-input"
                    formatter={handleInputNumberFormatter}
                    min={0}
                    onBlur={filterProduct.bind(this, 'minPrice', minPrice)}
                    onChange={changeMinPrice}
                    parser={handleInputNumberParser}
                    placeholder="min price"
                    step={1}
                    value={minPrice}
                    style={{
                      width: '100%',
                    }}
                  />
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                    }}>
                    -
                  </div>

                  <InputNumber
                    className="min-max-input"
                    formatter={handleInputNumberFormatter}
                    min={0}
                    onBlur={filterProduct.bind(this, 'maxPrice', maxPrice)}
                    onChange={changeMaxPrice}
                    parser={handleInputNumberParser}
                    placeholder="max price"
                    step={1}
                    value={maxPrice}
                    style={{
                      width: '100%',
                    }}
                  />
                </div>
              </Col>

              <Col xs={4} className="pd-margin-top-sm">
                <div
                  ref={bulkRef}
                  onClick={() => setOpenBulk(!openBulk)}
                  className="custom-select"
                  style={{
                    border: openBulk
                      ? '1px solid #40a9ff'
                      : '1px solid #d9d9d9',
                  }}>
                  Bulk Menu{' '}
                  <span>{openBulk ? <UpOutlined /> : <DownOutlined />} </span>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="custom-option"
                    style={{
                      opacity: !openBulk ? '0' : '1',
                      visibility: !openBulk ? 'hidden' : 'visible',
                      bottom: -190,
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        color: 'black',
                      }}>
                      <PdCsvUploader
                        afterUpload={handleAfterUploadBulkCreate}
                        buttonTitle="Bulk Create"
                        purpose="bulk_create"
                        transformHeader={transformHeaderBulkCreate}
                        noButton
                      />
                      <div
                        style={{
                          borderTop: '1px solid #AEAEB2',
                          borderBottom: '1px solid #AEAEB2',
                        }}>
                        <PdCsvUploader
                          afterUpload={handleAfterUploadBulkUpdate}
                          buttonTitle="Bulk Update"
                          purpose="bulk_create"
                          transformHeader={transformHeaderBulkUpdate}
                          noButton
                        />
                      </div>
                      <div
                        style={{
                          borderBottom: '1px solid #AEAEB2',
                        }}>
                        <PdCsvUploader
                          afterUpload={handleAfterUploadBulkDiscount}
                          buttonTitle="Bulk Discount"
                          purpose="bulk_discount"
                          transformHeader={transformHeaderBulkDiscount}
                          noButton
                        />
                      </div>
                      <PdCsvUploader
                        afterUpload={handleAfterUpload}
                        buttonTitle="Upload Keyword"
                        purpose="keyword"
                        type="default"
                        noButton
                      />
                    </div>
                  </div>
                </div>
              </Col>
              <Col xs={5} className="pd-margin-top-sm">
                <div
                  ref={downloadRef}
                  onClick={() => setOpen(!open)}
                  className="custom-select"
                  style={{
                    border: open ? '1px solid #40a9ff' : '1px solid #d9d9d9',
                  }}>
                  Download
                  <span>{open ? <UpOutlined /> : <DownOutlined />} </span>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="custom-option"
                    style={{
                      opacity: !open ? '0' : '1',
                      visibility: !open ? 'hidden' : 'visible',
                      bottom: -145,
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        color: 'black',
                      }}>
                      <PdCsvDownloader
                        datas={handleDownloadCsv}
                        filename="homestory-product-report"
                        noButton
                        columns={LIST_PRODUCTS_COLUMNS(warehouseList)}
                        separator=";"
                      />
                      <div
                        style={{
                          borderTop: '1px solid #AEAEB2',
                          borderBottom: '1px solid #AEAEB2',
                        }}>
                        <PdCsvDownloader
                          buttonLabel="Download Categories"
                          columns={LIST_CATEGORIES_COLUMNS}
                          datas={downloadListCategory}
                          filename="daftar-kategori"
                          noButton
                        />
                      </div>
                      <PdCsvDownloader
                        buttonLabel="Download Brands"
                        columns={LIST_BRANDS_COLUMNS}
                        datas={downloadListBrand}
                        filename="daftar-brands"
                        noButton
                      />
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={5} className="pd-margin-top-sm">
                <div
                  ref={templateRef}
                  onClick={() => setOpenTemplate(!openTemplate)}
                  className="custom-select"
                  style={{
                    border: openTemplate
                      ? '1px solid #40a9ff'
                      : '1px solid #d9d9d9',
                    width: 170,
                  }}>
                  Template
                  <span>
                    {openTemplate ? <UpOutlined /> : <DownOutlined />}{' '}
                  </span>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="custom-option templates"
                    style={{
                      opacity: !openTemplate ? '0' : '1',
                      visibility: !openTemplate ? 'hidden' : 'visible',
                      bottom: -190,
                      color: 'black',
                    }}>
                    {TEMPLATES_PRODUCT.map((item) => (
                      <div
                        className="template-download"
                        key={item.name}
                        onClick={() => window.open(item.template)}>
                        {item.name}
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col xs={4} className="pd-margin-top-sm"></Col>
            </Row>
          </Content>

          <Table
            dataSource={productList}
            columns={columns}
            rowKey={(record) => record.id}
            loading={loadStatus}
            onChange={handleTableChange}
            pagination={{
              total: totalData,
              current: Number(page),
            }}
          />
        </div>
      </div>
      <Modal
        centered
        className="modal--bannerImage"
        onCancel={closeModal}
        onOk={closeModal}
        title="Edit Banner"
        open={showModalBanner}>
        <div className="modal-container">
          <Form form={form} onFinish={handleSubmit}>
            <Form.Item label="Desktop Image (1200x200 px)">
              <PdUploader
                afterUpload={handleChangeRentPropModal}
                imgClassName="w-full"
                imgUrl={rentPropModal[FIELDS.DESKTOP_MEDIA_URL]}
                keyValue={FIELDS.DESKTOP_MEDIA_URL}
                name="Desktop Image (1280x400 px)"
              />
            </Form.Item>
            <Form.Item label="Mobile Image (640x320 px)">
              <PdUploader
                afterUpload={handleChangeRentPropModal}
                imgClassName="w-70"
                imgUrl={rentPropModal[FIELDS.MOBILE_MEDIA_URL]}
                keyValue={FIELDS.MOBILE_MEDIA_URL}
                name="Mobile Image (640x320 px)"
              />
            </Form.Item>
            <Form.Item>
              <div className="buttonSection mt-2">
                <PdButton
                  full
                  htmlType="submit"
                  loading={loadingButton}
                  type="primary">
                  Publish
                </PdButton>
              </div>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </Layout>
  );
}
